import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Tag,
} from 'antd';
import {
  GlobalOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import api, { get } from '../../utils/api';

const { TextArea } = Input;

const RegionManagement = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    keyword: '',
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRegions();
  }, [pagination.current, pagination.pageSize, filters]);

  // 获取区域列表
  const fetchRegions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      };
      const res = await get('/admin/regions', params);
      if (res.code === 200) {
        setRegions(res.data?.list || []);
        setPagination(prev => ({
          ...prev,
          total: res.data?.total || 0,
        }));
      }
    } catch (error) {
      message.error('获取区域列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 打开新增弹窗
  const handleAdd = () => {
    setEditingRegion(null);
    form.resetFields();
    form.setFieldsValue({
      exchangeRate: 1.0,
      isActive: true,
      sortOrder: 0,
      taxRate: 0,
    });
    setModalVisible(true);
  };

  // 打开编辑弹窗
  const handleEdit = (region) => {
    setEditingRegion(region);
    form.setFieldsValue({
      regionCode: region.region_code,
      regionName: region.region_name,
      regionNameEn: region.region_name_en,
      currencyCode: region.currency_code,
      currencySymbol: region.currency_symbol,
      currencyName: region.currency_name,
      exchangeRate: region.exchange_rate,
      languageCode: region.language_code,
      timezone: region.timezone,
      phonePrefix: region.phone_prefix,
      dateFormat: region.date_format,
      timeFormat: region.time_format,
      decimalSeparator: region.decimal_separator,
      thousandSeparator: region.thousand_separator,
      taxRate: region.tax_rate,
      isActive: region.is_active === 1,
      sortOrder: region.sort_order,
      extraConfig: region.extra_config ? JSON.stringify(region.extra_config, null, 2) : '',
    });
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const data = {
        ...values,
        isActive: values.isActive ? 1 : 0,
        extraConfig: values.extraConfig ? JSON.parse(values.extraConfig) : null,
      };

      if (editingRegion) {
        // 更新
        const res = await api.put(`/admin/regions/${editingRegion.id}`, data);
        if (res.code === 200) {
          message.success('更新成功');
          setModalVisible(false);
          fetchRegions();
        }
      } else {
        // 新增
        const res = await api.post('/admin/regions', data);
        if (res.code === 200) {
          message.success('创建成功');
          setModalVisible(false);
          fetchRegions();
        }
      }
    } catch (error) {
      message.error(error.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/admin/regions/${id}`);
      if (res.code === 200) {
        message.success('删除成功');
        fetchRegions();
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  // 切换状态
  const handleToggleStatus = async (region) => {
    try {
      const newStatus = region.is_active === 1 ? 0 : 1;
      const res = await api.put(`/admin/regions/${region.id}/status`, {
        isActive: newStatus,
      });
      if (res.code === 200) {
        message.success('状态更新成功');
        fetchRegions();
      }
    } catch (error) {
      message.error(error.message || '更新失败');
    }
  };

  // 表格列配置
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '区域代码',
      dataIndex: 'region_code',
      key: 'region_code',
      width: 100,
    },
    {
      title: '区域名称',
      key: 'region_name',
      render: (_, record) => (
        <div>
          <div>{record.region_name}</div>
          {record.region_name_en && (
            <div style={{ fontSize: '12px', color: '#999' }}>{record.region_name_en}</div>
          )}
        </div>
      ),
       width: 200,
    },
    {
      title: '货币',
      key: 'currency',
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.currency_symbol}</strong> {record.currency_code}
          </div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.currency_name}</div>
        </div>
      ),
    },
    {
      title: '语言代码',
      dataIndex: 'language_code',
      key: 'language_code',
      width: 150,
    },
    {
      title: '汇率',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      width: 150,
      render: (rate) => parseFloat(rate).toFixed(4),
    },
    {
      title: '电话前缀',
      dataIndex: 'phone_prefix',
      key: 'phone_prefix',
      width: 150,
    },
    {
      title: '税率',
      dataIndex: 'tax_rate',
      key: 'tax_rate',
      width: 150,
      render: (rate) => rate ? `${rate}%` : '-',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 150,
      render: (_, record) => (
        <Switch
          checked={record.is_active === 1}
          onChange={() => handleToggleStatus(record)}
        />
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个区域吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <GlobalOutlined />
          <span>国家/区域管理</span>
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          新增区域
        </Button>
      }
    >
      {/* 筛选条件 */}
      <Space style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 200 }}
          placeholder="搜索区域代码或名称"
          value={filters.keyword}
          onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
          onPressEnter={fetchRegions}
        />
        <Button onClick={fetchRegions}>搜索</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={regions}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize });
          },
        }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRegion ? '编辑区域' : '新增区域'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="区域代码"
            name="regionCode"
            rules={[{ required: true, message: '请输入区域代码' }]}
            extra="使用ISO 3166-1 alpha-2标准，如：CN, US, JP"
          >
            <Input placeholder="请输入区域代码" disabled={!!editingRegion} />
          </Form.Item>

          <Form.Item
            label="区域名称"
            name="regionName"
            rules={[{ required: true, message: '请输入区域名称' }]}
          >
            <Input placeholder="请输入区域名称" />
          </Form.Item>

          <Form.Item
            label="区域英文名称"
            name="regionNameEn"
          >
            <Input placeholder="请输入区域英文名称" />
          </Form.Item>

          <Form.Item
            label="货币代码"
            name="currencyCode"
            rules={[{ required: true, message: '请输入货币代码' }]}
            extra="使用ISO 4217标准，如：CNY, USD, JPY"
          >
            <Input placeholder="请输入货币代码" />
          </Form.Item>

          <Form.Item
            label="货币符号"
            name="currencySymbol"
            rules={[{ required: true, message: '请输入货币符号' }]}
          >
            <Input placeholder="请输入货币符号，如：¥, $, €" />
          </Form.Item>

          <Form.Item
            label="货币名称"
            name="currencyName"
            rules={[{ required: true, message: '请输入货币名称' }]}
          >
            <Input placeholder="请输入货币名称" />
          </Form.Item>

          <Form.Item
            label="汇率"
            name="exchangeRate"
            rules={[{ required: true, message: '请输入汇率' }]}
            extra="相对于人民币（CNY）的汇率"
          >
            <InputNumber
              min={0}
              step={0.0001}
              precision={4}
              style={{ width: '100%' }}
              placeholder="请输入汇率"
            />
          </Form.Item>

          <Form.Item
            label="语言代码"
            name="languageCode"
          >
            <Input placeholder="如：zh-CN, en-US" />
          </Form.Item>

          <Form.Item
            label="时区"
            name="timezone"
          >
            <Input placeholder="如：Asia/Shanghai, America/New_York" />
          </Form.Item>

          <Form.Item
            label="电话区号前缀"
            name="phonePrefix"
          >
            <Input placeholder="如：+86, +1" />
          </Form.Item>

          <Form.Item
            label="税率（%）"
            name="taxRate"
          >
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="请输入税率"
            />
          </Form.Item>

          <Form.Item
            label="排序权重"
            name="sortOrder"
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="数字越小越靠前"
            />
          </Form.Item>

          <Form.Item
            label="是否启用"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="额外配置"
            name="extraConfig"
            extra="JSON格式，可存储支付方式、物流配置等"
          >
            <TextArea
              rows={4}
              placeholder='{"key": "value"}'
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default RegionManagement;
